import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, Typography, Box, Stack, IconButton, Skeleton } from '@mui/material';

import ChartTooltip from '../Home/ChartTooltip';
import { ArrowLeft, ArrowRightAlt, ArrowRightOutlined, East, KeyboardBackspace } from '@mui/icons-material';

const COLORS = {
  high: '#148DFD',
  medium: '#A0D4FF',
  low: '#D6EDFF',
  veryLow: '#F7F8F9',
};

export default function AgentsHeatmap({ timeFilter = 'weekly', data, loading = false }) {
  const [tooltip, setTooltip] = useState({ visible: false, position: null, data: null });
  const [currentMonthIndex, setCurrentMonthIndex] = useState(0);
  const MONTHS_PER_VIEW = 6;

  console.log('timeFilter:', timeFilter);

  const isToday = timeFilter.toLowerCase() === 'today';
  const isYesterday = timeFilter.toLowerCase() === 'yesterday';
  const isSingleDay = isToday || isYesterday;
  const isWeekly = timeFilter.toLowerCase() === 'weekly' || timeFilter.toLowerCase() === '7 days' || timeFilter.toLowerCase() === '7d';
  const isMonthly = timeFilter.toLowerCase() === 'monthly' || timeFilter.toLowerCase() === '30 days';
  const isAnnually = timeFilter.toLowerCase() === 'annually' || timeFilter.toLowerCase() === '12 months';

  // Check if this is a 12-month view that needs pagination
  const is12MonthView = useMemo(() => {
    return isAnnually && data?.months && data.months.length === 12;
  }, [isAnnually, data]);

  // Calculate total pages for 12-month view
  const totalPages = useMemo(() => {
    if (!is12MonthView) return 1;
    return Math.ceil(data.months.length / MONTHS_PER_VIEW);
  }, [is12MonthView, data]);

  // Reset to first page when data or filter changes
  useEffect(() => {
    setCurrentMonthIndex(0);
  }, [timeFilter, data]);

  // Slice data for 12-month pagination
  const displayData = useMemo(() => {
    if (!is12MonthView) return data;

    const start = currentMonthIndex * MONTHS_PER_VIEW;
    const end = Math.min(start + MONTHS_PER_VIEW, data.months.length);

    return {
      ...data,
      months: data.months.slice(start, end),
      matrix: data.matrix.map(row => row.slice(start, end)),
      agentCounts: data.agentCounts ? data.agentCounts.slice(start * 8, end * 8) : null
    };
  }, [is12MonthView, currentMonthIndex, data, MONTHS_PER_VIEW]);

  // Navigation handlers
  const handlePrevious = () => {
    setCurrentMonthIndex(prev => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentMonthIndex(prev => Math.min(totalPages - 1, prev + 1));
  };

  const canGoPrevious = currentMonthIndex > 0;
  const canGoNext = currentMonthIndex < totalPages - 1;

  // Determine if short-term view (pad to 7 columns)
  const isShortTermView = isSingleDay || timeFilter.toLowerCase() === 'custom' || isWeekly;

  // Compute number of groups (columns)
  const numGroups = useMemo(() => {
    return displayData?.days?.length || displayData?.dayGroups?.length || displayData?.months?.length || (isSingleDay ? 1 : 0);
  }, [displayData, isSingleDay]);

  // Padding logic for short-term views
  const { paddedMatrix, paddedColLabels, paddedAgentCounts, usePeriodLabelRow, periodLabel } = useMemo(() => {
    if (!isShortTermView || numGroups > 7 || !displayData) {
      return { paddedMatrix: null, paddedColLabels: null, paddedAgentCounts: null, usePeriodLabelRow: false, periodLabel: '' };
    }

    const targetCols = 7;
    const actualCols = Math.max(1, numGroups);
    const numHours = displayData.hours?.length || 8;
    // Restore empty-state grid when matrix is missing/empty but hours exist
    const baseMatrix =
      Array.isArray(displayData.matrix) && displayData.matrix.length > 0
        ? displayData.matrix
        : Array.from({ length: numHours }, () => Array(Math.max(1, numGroups || 1)).fill('veryLow'));

    if (actualCols >= targetCols) {
      if (baseMatrix === displayData.matrix) {
        return { paddedMatrix: null, paddedColLabels: null, paddedAgentCounts: null, usePeriodLabelRow: false, periodLabel: '' };
      }
      return {
        paddedMatrix: baseMatrix,
        paddedColLabels: null,
        paddedAgentCounts: null,
        usePeriodLabelRow: false,
        periodLabel: '',
      };
    }

    const padCols = targetCols - actualCols;

    let _paddedMatrix = null;
    let _paddedColLabels = null;
    let _paddedAgentCounts = null;
    let _usePeriodLabelRow = false;
    let _periodLabel = '';

    if (actualCols === 1) {
      // Single day: use period label row, pad matrix and agentCounts
      _usePeriodLabelRow = true;
      _periodLabel = displayData.days?.[0] || displayData.dayGroups?.[0] || displayData.months?.[0] || 'Selected Day';
      _paddedMatrix = baseMatrix.map(row => [row[0] || 'veryLow', ...Array(padCols).fill('veryLow')]);
      const padAgentData = Array(padCols * numHours).fill({ agentCount: 0 });
      _paddedAgentCounts = [...(displayData.agentCounts || []), ...padAgentData];
    } else {
      // Multi-day (2-6): pad col labels and matrix/agentCounts
      const actualColLabels = displayData.days || displayData.dayGroups || displayData.months || [];
      _paddedColLabels = [...actualColLabels, ...Array(padCols).fill('-')];
      _paddedMatrix = baseMatrix.map(row => [...row, ...Array(padCols).fill('veryLow')]);
      const padAgentData = Array(padCols * numHours).fill({ agentCount: 0 });
      _paddedAgentCounts = [...(displayData.agentCounts || []), ...padAgentData];
    }

    return {
      paddedMatrix: _paddedMatrix,
      paddedColLabels: _paddedColLabels,
      paddedAgentCounts: _paddedAgentCounts,
      usePeriodLabelRow: _usePeriodLabelRow,
      periodLabel: _periodLabel
    };
  }, [isShortTermView, numGroups, displayData]);

  // Apply padding to displayData; fill empty matrix so empty-state cells still render
  const effectiveDisplayData = useMemo(() => {
    const source = paddedMatrix
      ? {
          ...displayData,
          matrix: paddedMatrix,
          agentCounts: paddedAgentCounts || displayData?.agentCounts,
        }
      : displayData;

    const hours = source?.hours || [];
    const cols =
      source?.days?.length ||
      source?.dayGroups?.length ||
      source?.months?.length ||
      (isShortTermView ? 7 : 1);

    if (hours.length > 0 && (!Array.isArray(source?.matrix) || source.matrix.length === 0)) {
      return {
        ...source,
        matrix: Array.from({ length: hours.length }, () => Array(Math.max(1, cols)).fill('veryLow')),
      };
    }

    return source;
  }, [displayData, paddedMatrix, paddedAgentCounts, isShortTermView]);

  // Effective column labels
  const effectiveColLabels = useMemo(() => {
    if (usePeriodLabelRow) return null;
    if (paddedColLabels) return paddedColLabels;
    return displayData?.days || displayData?.dayGroups || displayData?.months;
  }, [usePeriodLabelRow, paddedColLabels, displayData]);

  // Dynamic orientation
  const isVerticalHours = useMemo(() => {
    return isShortTermView || (!isSingleDay && effectiveDisplayData?.matrix?.length === effectiveDisplayData?.hours?.length);
  }, [isShortTermView, isSingleDay, effectiveDisplayData]);

  const isEffectiveSingleDay = usePeriodLabelRow || isSingleDay;

  // Row labels: hours for vertical, periods otherwise
  const getRowLabels = () => {
    if (isShortTermView || isVerticalHours) {
      return effectiveDisplayData?.hours || [];
    }
    return effectiveDisplayData?.days || effectiveDisplayData?.weeks || effectiveDisplayData?.months || [];
  };

  const rowLabels = getRowLabels();

  // Column labels
  const getColLabels = () => {
    if (isEffectiveSingleDay) return null;
    return effectiveColLabels;
  };

  const colLabels = getColLabels();

  // Calculate cell size and layout
  const getLayoutConfig = () => {
    const actualNumCols = isShortTermView ? 7 : (colLabels?.length || 0);
    if (isShortTermView) {
      return {
        cellSize: { minWidth: '56px', height: '32px' },
        labelWidth: '50px',
        borderRadius: '8px',
        spacing: '8px',
        bottomPl: '55px',
        numCols: 7,
      };
    }
    if (actualNumCols >= 20) {
      return {
        cellSize: { minWidth: '56px', height: '32px' },
        labelWidth: '50px',
        borderRadius: '6px',
        spacing: '8px',
        bottomPl: '55px',
        numCols: actualNumCols,
      };
    }
    if (isMonthly) {
      return {
        cellSize: { minWidth: '38px', height: '32px' },
        labelWidth: '50px',
        borderRadius: '8px',
        spacing: '8px',
        bottomPl: '55px',
        numCols: actualNumCols,
      };
    }
    if (isAnnually) {
      return {
        cellSize: { minWidth: '66px', height: '32px' },
        labelWidth: '50px',
        borderRadius: '8px',
        spacing: '8px',
        bottomPl: '55px',
        numCols: actualNumCols,
      };
    }
    return {
      cellSize: { minWidth: '52px', height: '32px' },
      labelWidth: '50px',
      borderRadius: '8px',
      spacing: '8px',
      bottomPl: '55px',
      numCols: actualNumCols,
    };
  };

  const config = getLayoutConfig();

  const labelW = parseInt(config.labelWidth);
  const cellW = parseInt(config.cellSize.minWidth);
  const space = parseInt(config.spacing);
  const minWidth = `${labelW + config.numCols * (cellW + space)}px`;

  // Get row data (now padded where applicable)
  const getRowData = (rowIdx) => {
    return effectiveDisplayData?.matrix?.[rowIdx] || [];
  };

  // Get time label
  const getTimeLabel = (rowIdx, colIdx) => {
    if (isShortTermView) {
      if (numGroups === 1) {
        return `${rowLabels[rowIdx]} ${periodLabel}`;
      } else {
        const colLabel = colLabels?.[colIdx] || '-';
        return `${rowLabels[rowIdx]} on ${colLabel}`;
      }
    }
    const rowLabel = rowLabels[rowIdx];
    const colLabel = colLabels ? colLabels[colIdx] : `Hour ${colIdx}`;
    if (isVerticalHours) {
      return `${rowLabel} on ${colLabel}`;
    } else {
      return `${rowLabel} at ${colLabel}`;
    }
  };

  // Handle mouse enter
  const handleMouseEnter = (event, rowIdx, colIdx) => {
    const rowData = getRowData(rowIdx);
    const colIntensity = rowData[colIdx];
    if (colIntensity === 'veryLow' && colIdx >= numGroups && numGroups < 7) return; // Skip tooltip for pure padding

    const rect = event.currentTarget.getBoundingClientRect();
    const timeLabel = getTimeLabel(rowIdx, colIdx);
    const level = colIntensity?.charAt(0)?.toUpperCase() + colIntensity?.slice(1) || 'Very Low';

    // Get agent count
    let agentCount = 0;
    if (effectiveDisplayData?.agentCounts && Array.isArray(effectiveDisplayData.agentCounts)) {
      const numHours = effectiveDisplayData.hours?.length || 8;
      let dataIndex;

      if (isShortTermView) {
        // Always vertical for short-term: col * hours + row
        dataIndex = colIdx * numHours + rowIdx;
      } else if (isVerticalHours) {
        dataIndex = colIdx * numHours + rowIdx;
      } else {
        dataIndex = rowIdx * numHours + colIdx;
      }

      const item = effectiveDisplayData.agentCounts[dataIndex];
      agentCount = item?.agentCount || 0;
    }

    setTooltip({
      visible: true,
      position: {
        x: rect.left + rect.width / 2,
        y: rect.top
      },
      data: {
        title: `Agent Availability`,
        stats: [
          { label: 'Agent Count', value: agentCount },
          { label: 'Level', value: level }
        ]
      }
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, position: null, data: null });
  };

  if (loading) {
    const numRows = 8; // Default to 8 rows (hours)
    const numCols = config.numCols || 7;

    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: '8px',
          p: 0,
          minWidth: '544px',
          width: '100%',
          border: '1px solid #E5E9EC',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box>
              <Skeleton variant="text" width={200} height={24} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width={150} height={16} />
            </Box>
            {is12MonthView && (
              <Stack direction="row" spacing={1}>
                <Skeleton variant="circular" width={32} height={32} />
                <Skeleton variant="circular" width={32} height={32} />
              </Stack>
            )}
          </Box>

          <Box sx={{ mt: '32px' }}>
            <Box sx={{ minWidth }}>
              {/* Skeleton rows */}
              {Array.from({ length: numRows }).map((_, rowIdx) => (
                <Stack
                  key={rowIdx}
                  direction="row"
                  spacing={config.spacing}
                  alignItems="center"
                  sx={{ mb: 0.5 }}
                >
                  <Skeleton
                    variant="text"
                    width={config.labelWidth}
                    height={16}
                    sx={{ flexShrink: 0 }}
                  />
                  {Array.from({ length: numCols }).map((_, colIdx) => (
                    <Box
                      key={colIdx}
                      sx={{
                        width: '100%',
                        minWidth: config.cellSize.minWidth,
                        height: config.cellSize.height,
                      }}
                    >
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        sx={{
                          borderRadius: config.borderRadius,
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              ))}

              {/* Skeleton column labels */}
              {!isEffectiveSingleDay && (
                <Stack
                  direction="row"
                  spacing={config.spacing}
                  sx={{ mt: 1, pl: config.bottomPl }}
                >
                  {Array.from({ length: numCols }).map((_, colIdx) => (
                    <Box
                      key={colIdx}
                      sx={{
                        width: '100%',
                        minWidth: config.cellSize.minWidth,
                        textAlign: 'center',
                      }}
                    >
                      <Skeleton
                        variant="text"
                        width="70%"
                        height={14}
                        sx={{
                          mx: 'auto',
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              )}

              {/* Skeleton period label for effective single day */}
              {isEffectiveSingleDay && (
                <Stack
                  direction="row"
                  spacing={config.spacing}
                  alignItems="center"
                  sx={{ mt: 1, mb: 2 }}
                >
                  <Skeleton
                    variant="text"
                    width={config.labelWidth}
                    height={16}
                    sx={{ flexShrink: 0 }}
                  />
                  {Array.from({ length: numCols }).map((_, colIdx) => (
                    <Box
                      key={colIdx}
                      sx={{
                        width: '100%',
                        minWidth: config.cellSize.minWidth,
                        textAlign: 'center',
                      }}
                    >
                      <Skeleton
                        variant="text"
                        width="70%"
                        height={18}
                        sx={{
                          mx: 'auto',
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>

          {/* Legend skeleton */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{ mt: 2 }}
          >
            <Skeleton variant="text" width={40} height={14} />
            {Array.from({ length: 4 }).map((_, idx) => (
              <Skeleton
                key={idx}
                variant="rectangular"
                width={32}
                height={10}
                sx={{ borderRadius: '3px' }}
              />
            ))}
            <Skeleton variant="text" width={40} height={14} />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '8px',
        p: 0,
        minWidth: '544px',
        width: '100%',
        border: '1px solid #E5E9EC',
      }}
    >
      <CardContent style={{ padding: '24px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" sx={{
              color: '#061522',
              fontFamily: 'Inter',
              fontSize: '18px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '18px',
            }}>
              Agent availability heatmap
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{
              color: '#405261',
              fontFamily: 'Inter',
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: 400,
              marginTop: '8px',
              lineHeight: '18px'
            }}>
              Agent availability by time
            </Typography>
          </Box>
          {is12MonthView && (
            <>
              {canGoPrevious && (
                <IconButton onClick={handlePrevious}>
                  <East style={{ transform: 'rotate(180deg)', color: '#148DFD' }} />
                </IconButton>
              )}
              {canGoNext && (
                <IconButton onClick={handleNext}>
                  <East style={{ color: '#148DFD' }} />
                </IconButton>
              )}
            </>
          )}
          {/* <Select
            value={csatAgentFilter}
            onChange={(e) => setCsatAgentFilter(e.target.value)}
            // disabled={isSingleDay}
            size="small"
            IconComponent={Dropdown}
            sx={{
              height: "40px",
              width: "152px",
              fontSize: "14px",
              "& .MuiSelect-select": {
                fontSize: "14px",
                paddingLeft: "16px",
              },
              "& .MuiInputBase-input": {
                paddingLeft: "16px !important",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#D5DCE1 !important",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#D5DCE1 !important",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#D5DCE1 !important",
              },
              "& .MuiSelect-icon": {
                fontSize: "16px",
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                right: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiSelect-iconOpen": {
                fontSize: "16px",
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%) rotate(180deg)",
                right: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  border: "1px solid #D5DCE1",
                  marginTop: "4px",
                  width: "120px",
                  padding: ' 0px 8px',
                  borderRadius: "8px",
                  "& .MuiMenuItem-root": {
                    fontSize: "14px",
                    padding: "8px 8px",
                    color: "#405261",
                    "&:hover": {
                      color: '#061522',
                      borderRadius: "8px",
                      background: "#F9FAFA  !important",
                    },
                    "&.Mui-selected": {
                      color: '#061522',
                      borderRadius: "8px",
                      background: "#F9FAFA !important",
                    },
                    "&.Mui-selected:hover": {
                      background: "#F9FAFA  !important",
                    },
                  },
                },
              },
            }}
          >
            {renderMenuItems()}
          </Select> */}
        </Box>

        <Box sx={{ mt: '32px' }}>
          <Box sx={{ minWidth }}>
            {/* Heatmap rows */}
            {rowLabels.map((label, rowIdx) => {
              const rowData = getRowData(rowIdx);
              return (
                <Stack
                  key={rowIdx}
                  direction="row"
                  spacing={config.spacing}
                  alignItems="center"
                  sx={{ mb: 0.5 }}
                >
                  <Box sx={{ width: config.labelWidth, flexShrink: 0 }}>
                    <Typography
                      sx={{
                        color: '#405261',
                        fontFamily: 'Inter',
                        fontSize: '12px',
                        fontWeight: 400,
                        lineHeight: '16px',
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                  {rowData.map((colIntensity, colIdx) => (
                    <Box
                      key={`${rowIdx}-${colIdx}`}
                      onMouseEnter={(e) => handleMouseEnter(e, rowIdx, colIdx)}
                      onMouseLeave={handleMouseLeave}
                      sx={{
                        width: '100%',
                        minWidth: config.cellSize.minWidth,
                        height: config.cellSize.height,
                        borderRadius: config.borderRadius,
                        bgcolor: COLORS[colIntensity] || COLORS.veryLow,
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        position: 'relative',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          zIndex: 1,
                        },
                      }}
                    />
                  ))}
                </Stack>
              );
            })}

            {/* Column labels (for multi-period, non-single) */}
            {!isEffectiveSingleDay && colLabels && (
              <Stack
                direction="row"
                spacing={config.spacing}
                sx={{ mt: 1, pl: config.bottomPl }}
              >
                {colLabels.map((colLabel, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      width: '100%',
                      minWidth: config.cellSize.minWidth,
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#405261',
                        fontFamily: 'Inter',
                        fontSize: '12px',
                        fontWeight: 400,
                        lineHeight: '14px',
                      }}
                    >
                      {colLabel}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}

            {/* Period label row for effective single day */}
            {isEffectiveSingleDay && (
              <Stack
                direction="row"
                spacing={config.spacing}
                alignItems="center"
                sx={{ mt: 1, mb: 2, opacity: 0.6 }}
              >
                <Box sx={{ width: config.labelWidth, flexShrink: 0 }}>
                  <Typography
                    sx={{
                      color: '#405261',
                      fontFamily: 'Inter',
                      fontSize: '12px',
                      fontWeight: 400,
                      lineHeight: '16px',
                    }}
                  />
                </Box>
                {Array.from({ length: config.numCols }).map((_, colIdx) => (
                  <Box
                    key={colIdx}
                    sx={{
                      width: '100%',
                      minWidth: config.cellSize.minWidth,
                      height: config.cellSize.height,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#405261',
                        fontFamily: 'Inter',
                        fontSize: '12px',
                        fontWeight: 400,
                        lineHeight: '18px',
                      }}
                    >
                      {colIdx === 0 ? periodLabel : '-'}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Box>

        {/* Legend */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{ mt: 2 }}
        >
          <Typography
            sx={{
              color: '#405261',
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '14px',
            }}
          >
            Low
          </Typography>
          <Box sx={{ width: '48px', height: '16px', borderRadius: '4px', bgcolor: COLORS.veryLow }} />
          <Box sx={{ width: '48px', height: '16px', borderRadius: '4px', bgcolor: COLORS.low }} />
          <Box sx={{ width: '48px', height: '16px', borderRadius: '4px', bgcolor: COLORS.medium }} />
          <Box sx={{ width: '48px', height: '16px', borderRadius: '4px', bgcolor: COLORS.high }} />
          <Typography
            sx={{
              color: '#405261',
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '14px',
            }}
          >
            High
          </Typography>
        </Stack>
      </CardContent>

      {/* Tooltip */}
      {tooltip.visible && tooltip.data && (
        <ChartTooltip
          title={tooltip.data.title}
          stats={tooltip.data.stats}
          position={tooltip.position}
        />
      )}
    </Card>
  );
}